import { swSettings } from "app/common/services/swSettings";
import { useCallback, useMemo } from "react";
import {
    ButtonsContainer,
    DownloadExcelContainer,
    SearchContainer,
    TableFilterContainer,
    TableInputContainer,
    TableTopContainer,
} from "../../components/CategoryLeadersTable/CategoryLeadersTableTop/CategoryLeadersTableTop.styles";
import type { ICategoryLeadersTableTopProps } from "../../components/CategoryLeadersTable/CategoryLeadersTableTop/CategoryLeadersTableTop.types";
import {
    combineTableFilters,
    getColumnsPickerLiteProps,
    getTableExcelUrl,
} from "../../components/CategoryLeadersTable/CategoryLeadersTableTop/CategoryLeadersTableTopUtils";
import { useTableSearch } from "../../components/CategoryLeadersTable/CategoryLeadersTableTop/Hooks/UseTableSearch";
import { SearchInput } from "@similarweb/ui-components";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { AddToDashboardWrapper } from "../../components/CategoryLeadersTable/CategoryLeadersTableTop/Components/AddToDashboard";
import { usePaidOrganicFilter } from "../../components/PaidOrganicFilter/usePaidOrganicFilter";
import { PaidOrganicFilter } from "../../components/PaidOrganicFilter/PaidOrganicFilter";
import { PaidOrganicFilterEnum } from "../../components/PaidOrganicFilter/PaidOrganicFilter.types";
import { useDidMountEffect } from "app/custom-hooks/useDidMountEffect";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { ECategoryLeadersMetricType } from "app/pages/industry-analysis/category-leaders/CategoryLeaders.types";
import type { ICategoryLeadersTableSort } from "../../components/CategoryLeadersTable/CategoryLeadersTable.shared.types";

const CHANNEL_DISPLAY_NAME: Partial<Record<ECategoryLeadersMetricType, string>> = {
    [ECategoryLeadersMetricType.CategoryLeadersSearch]: "Search",
    [ECategoryLeadersMetricType.CategoryLeadersSocial]: "Social",
};

export const CategoryLeadersSearchTableTop = (props: ICategoryLeadersTableTopProps) => {
    const {
        onFilterChange,
        tableColumns,
        onClickToggleColumns,
        services,
        tableQueryParams,
        filtersStateObject,
        navParams,
        sort,
        excelApiEndpoint,
        metricType,
        totalCount,
    } = props;

    const { searchFilter, onSearch } = useTableSearch();
    const { paidOrganicFilter, selectedPaidOrganicId, onSelectPaidOrganicId } = usePaidOrganicFilter();
    const isPaidExcelPermitted = isPaidExcelAllowed();

    const handleSelectPaidOrganicId = useCallback(
        (selectedId: PaidOrganicFilterEnum | null) => {
            if (selectedId !== null && typeof selectedId !== "undefined") {
                services.tracker.dispatchTrackEvent("Click Filter", "click", "Paid Organic Filter", {
                    channel: CHANNEL_DISPLAY_NAME[metricType] ?? ECategoryLeadersMetricType[metricType],
                    selection: PaidOrganicFilterEnum[selectedId],
                });
            }
            onSelectPaidOrganicId(selectedId);
        },
        [onSelectPaidOrganicId, services.tracker, metricType],
    );

    const tableFilters = useMemo(() => {
        return combineTableFilters(searchFilter, paidOrganicFilter);
    }, [searchFilter, paidOrganicFilter]);

    const selectedCategory = useMemo(() => {
        return services.categoryService.categoryQueryParamToCategoryObject(navParams.category);
    }, [navParams, services.categoryService]);

    const columnPickerProps = useMemo(() => {
        return getColumnsPickerLiteProps(tableColumns, onClickToggleColumns);
    }, [tableColumns, onClickToggleColumns]);

    const tableExcelUrl = getTableExcelUrl({
        apiEndpoint: excelApiEndpoint,
        tableQueryParams: tableQueryParams,
        tableFilter: tableFilters,
        category: selectedCategory,
        services: services,
        tableSort: sort,
    });

    const isExcelEnabled = swSettings.current.resources.IsExcelAllowed;

    useDidMountEffect(() => {
        onFilterChange(
            {
                filter: `${tableFilters}`,
            },
            false,
        );
    }, [tableFilters, onFilterChange]);

    return (
        <TableTopContainer data-automation="category-leaders-table-top">
            <TableFilterContainer>
                <PaidOrganicFilter
                    selectedId={selectedPaidOrganicId}
                    onSelectId={handleSelectPaidOrganicId}
                    services={services}
                />
            </TableFilterContainer>
            <TableInputContainer>
                <SearchContainer>
                    <SearchInput
                        data-automation="category-leaders-search-input"
                        defaultValue={""}
                        debounce={250}
                        onChange={onSearch}
                        placeholder={services.translate("table.search.placeholder.default")}
                    />
                </SearchContainer>
                <ButtonsContainer>
                    <DownloadExcelContainer>
                        <DownloadButtonMenu
                            data-automation="category-leaders-download-excel"
                            Excel={true}
                            PaidExcel={isPaidExcelPermitted}
                            totalCount={totalCount}
                            downloadUrl={tableExcelUrl}
                            excelLocked={!isExcelEnabled}
                        />
                    </DownloadExcelContainer>
                    <div>
                        <ColumnsPickerLite
                            {...columnPickerProps}
                            withTooltip={true}
                            translations={{
                                tooltip: services.translate("workspace.sales.column.picker"),
                                getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                    services.translate("common.columnsPicker.selectedAmount", {
                                        selected: selectedValue,
                                        total: totalValue,
                                    }),
                                mandatoryField: services.translate("common.columnsPicker.mandatoryField"),
                                apply: services.translate("common.apply"),
                            }}
                        />
                    </div>
                    <div>
                        <AddToDashboardWrapper
                            filtersStateObject={filtersStateObject}
                            navParams={navParams}
                            tableFilters={tableFilters}
                            metricType={metricType}
                        />
                    </div>
                </ButtonsContainer>
            </TableInputContainer>
        </TableTopContainer>
    );
};
